// EXTERNAL DEPENDENCIES
import React from 'react'

// HELPERS
import { invokeOnSpaceOrEnter, trackClick, usePrefixedContent } from '@/utils'

// STYLES
import styles from './styles.module.less'

// CONSTANTS
import { FILE_UPLOADER_CONTAINER_TEST_ID } from '@/constants/test-ids'
export const STANDARD_ACCEPTED_FILE_TYPES =
  'image/png, image/jpeg, image/jpg, image/bmp'
const TWELVE_MB_IN_BYTES = 12 * 1024 * 1024

// TYPES
import { UnformattedFptiOptions } from '@/types'

/**
 * Wraps the given children with an input that will open the system's files on click
 */
const FileUploader: React.FC<{
  children: React.ReactNode
  keyName: string
  ariaLabelText: string
  acceptedFileTypes?: string
  fpti?: UnformattedFptiOptions
  handleResult: (result: string | ArrayBuffer) => void
  handleError: (errorMessage: string) => void
}> = ({
  children,
  keyName,
  ariaLabelText,
  acceptedFileTypes = STANDARD_ACCEPTED_FILE_TYPES,
  fpti,
  handleResult,
  handleError,
}) => {
  const getContent = usePrefixedContent('components.ui.file-uploader.')
  const fileConstraintsErrorText = getContent('fileConstraintsError')
  const generalUploadErrorText = getContent('generalUploadError')
  const genericAriaLabelText = getContent('genericAriaLabelText')
  const elementName = `${keyName}-file-uploader`

  function handleFileSelection(event: React.ChangeEvent<HTMLInputElement>) {
    try {
      trackClick(fpti)

      const { target } = event
      const [uploadedFile] = target.files

      if (!uploadedFile || !uploadedFile.name) {
        return
      }

      const fileExt = uploadedFile.name
        ?.substring(uploadedFile.name.lastIndexOf('.') + 1)
        ?.toLowerCase()

      if (
        !fileExt ||
        acceptedFileTypes.indexOf(fileExt) < 0 ||
        uploadedFile.size > TWELVE_MB_IN_BYTES
      ) {
        handleError(fileConstraintsErrorText)
      } else {
        const fileReader = new global.window.FileReader()
        fileReader.readAsDataURL(uploadedFile)

        fileReader.onload = (e) => {
          handleError('')
          handleResult(e.target.result)
          target.value = '' // allows same file to be uploaded if previously canceled
        }
      }
    } catch (_e) {
      handleError(generalUploadErrorText)
    }
  }

  return (
    <>
      <label
        htmlFor={elementName}
        className={styles.labelContainer}
        aria-label={ariaLabelText || genericAriaLabelText}
      >
        <div
          tabIndex={0}
          onKeyDown={invokeOnSpaceOrEnter(() =>
            document.getElementById(elementName).click(),
          )}
          role='button'
        >
          {children}
        </div>
      </label>
      <input
        tabIndex={-1}
        type='file'
        id={elementName}
        name={elementName}
        data-testid={FILE_UPLOADER_CONTAINER_TEST_ID}
        onChange={handleFileSelection}
        className={styles.visuallyHidden}
        accept={acceptedFileTypes}
      />
    </>
  )
}

export default FileUploader
